export const STUDENT_STATUS_OPTIONS = [
  "Signed Up",
  "Enrolled",
  "Paused",
  "No Answer",
  "Not Interested",
  "Finished",
];

export const STUDENT_SCHEDULE_OPTIONS = [
  "DT Scheduled",
  "DC Scheduled",
  "Conference Scheduled",
];

export const STUDENT_INACTIVITY_PAUSE_DAYS = 20;

const STATUS_ALIASES = {
  active: "Signed Up",
  "signed up": "Signed Up",
  enrolled: "Enrolled",
  paused: "Paused",
  "no answer": "No Answer",
  "not interested": "Not Interested",
  finished: "Finished",
  deleted: "Finished",
  "dt completed": "Signed Up",
  "dc completed": "Signed Up",
  "dt scheduled": "Signed Up",
  "dc scheduled": "Signed Up",
  "conference scheduled": "Signed Up",
};

function toTimeMs(value) {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  
  // Force Javascript to treat "YYYY-MM-DD" as Local Time, not UTC
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const parsedLocal = new Date(`${raw}T00:00:00`).getTime();
    return Number.isFinite(parsedLocal) ? parsedLocal : 0;
  }
  
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

export function parseStudentLifecycleDate(value) {
  const ms = toTimeMs(value);
  if (!ms) return null;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatStudentDateKey(value) {
  const d = parseStudentLifecycleDate(value);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function normalizeStudentStatus(rawStatus) {
  const raw = String(rawStatus || "").trim();
  if (!raw) return "Signed Up";
  const mapped = STATUS_ALIASES[raw.toLowerCase()];
  if (mapped) return mapped;
  const exact = STUDENT_STATUS_OPTIONS.find((opt) => opt.toLowerCase() === raw.toLowerCase());
  return exact || "Signed Up";
}

export function normalizeStudentScheduleType(rawScheduleType, fallbackRawStatus = "") {
  const fromSchedule = String(rawScheduleType || "").trim();
  if (fromSchedule) {
    const normalized = STUDENT_SCHEDULE_OPTIONS.find(
      (opt) => opt.toLowerCase() === fromSchedule.toLowerCase()
    );
    if (normalized) return normalized;
  }

  const fromStatus = String(fallbackRawStatus || "").trim();
  if (!fromStatus) return "";
  const normalized = STUDENT_SCHEDULE_OPTIONS.find(
    (opt) => opt.toLowerCase() === fromStatus.toLowerCase()
  );
  return normalized || "";
}

export function applyStudentAutoStatusTransitions({
  currentStatus,
  hasAnySession = false,
  hasUpcomingSession = false,
  lastSessionDate = null,
  inactivityDays = STUDENT_INACTIVITY_PAUSE_DAYS,
  now = new Date(),
}) {
  const status = normalizeStudentStatus(currentStatus);

  if (status === "Signed Up" && hasAnySession) {
    return "Enrolled";
  }

  if (status === "Enrolled") {
    const last = parseStudentLifecycleDate(lastSessionDate);
    if (!last) return status;
    const diffMs = now.getTime() - last.getTime();
    const thresholdMs = Math.max(1, Number(inactivityDays) || STUDENT_INACTIVITY_PAUSE_DAYS) * 86400000;
    if (diffMs >= thresholdMs && !hasUpcomingSession) return "Paused";
  }

  if (status === "Paused") {
    if (hasUpcomingSession) return "Enrolled";
    const last = parseStudentLifecycleDate(lastSessionDate);
    if (last) {
      const diffMs = now.getTime() - last.getTime();
      const thresholdMs = Math.max(1, Number(inactivityDays) || STUDENT_INACTIVITY_PAUSE_DAYS) * 86400000;
      if (diffMs < thresholdMs) return "Enrolled";
    }
  }

  return status;
}
